import { useState, useEffect } from "react";
import { useSearch } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "wouter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2, Hash, MessageSquarePlus, Route, Target, Copy, Sparkles,
  RefreshCw, Lightbulb, Check, Pencil, Save, ThumbsUp, ThumbsDown, Mic,
  ShieldCheck, SlidersHorizontal, BarChart2, MessageCircle, Heart, Star, Scissors,
  Zap, ArrowRight,
} from "lucide-react";
import { VoiceMatchRing } from "@/components/voice-match-ring";
import {
  useGenerateHooks,
  useGenerateThread,
  useGenerateHashtags,
  useGenerateCTA,
  useRepurposePost,
  useExpandIdea,
  useCreateIdea,
  useSubmitVoiceFeedback,
} from "@workspace/api-client-react";
import type {
  GenerateHooksRequestPlatform,
  GenerateHashtagsRequestPlatform,
  GenerateCTARequestGoal,
  RepurposeRequestSourcePlatform,
  RepurposeRequestTargetPlatformsItem,
  ExpandIdeaRequestPlatform,
} from "@workspace/api-client-react";
import { useAppStore } from "@/store";
import { SocialIcon } from "@/components/social-icon";

type Platform = "twitter" | "linkedin" | "reddit" | "instagram";

const PLATFORMS: { value: Platform; label: string }[] = [
  { value: "twitter", label: "Twitter / X" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "reddit", label: "Reddit" },
  { value: "instagram", label: "Instagram" },
];

const CTA_GOALS: { value: GenerateCTARequestGoal; label: string }[] = [
  { value: "engage", label: "Engage" },
  { value: "follow", label: "Follow" },
  { value: "click", label: "Click" },
  { value: "share", label: "Share" },
  { value: "comment", label: "Comment" },
  { value: "buy", label: "Buy" },
];

function PlatformSelect({ value, onValueChange }: { value: Platform; onValueChange: (v: Platform) => void }) {
  return (
    <Select value={value} onValueChange={(v) => onValueChange(v as Platform)}>
      <SelectTrigger className="h-10 bg-background border-border">
        <div className="flex items-center gap-2">
          <SocialIcon platform={value} size={20} />
          <SelectValue placeholder="Select platform" />
        </div>
      </SelectTrigger>
      <SelectContent>
        {PLATFORMS.map((p) => (
          <SelectItem key={p.value} value={p.value}>
            <span className="flex items-center gap-2">
              <SocialIcon platform={p.value} size={20} />
              {p.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function CopyButton({ text }: { text: string }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast({ title: "Copied!" });
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      onClick={handleCopy}
      className="absolute right-3 top-4 text-muted-foreground hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity"
      aria-label={copied ? "Copied" : "Copy to clipboard"}
    >
      {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
    </button>
  );
}

function ResultCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="group relative bg-card border border-border rounded-xl p-4 pr-12 text-sm leading-relaxed shadow-sm hover:border-primary/50 transition-colors">
      {children}
    </div>
  );
}

function platformLabel(value: string): string {
  return PLATFORMS.find((p) => p.value === value)?.label ?? value;
}

const TOOL_TABS = [
  { value: "hooks", label: "Hook Lab", icon: Target },
  { value: "thread", label: "Thread Builder", icon: Route },
  { value: "repurpose", label: "Repurpose", icon: RefreshCw },
  { value: "hashtags", label: "Hashtags", icon: Hash },
  { value: "cta", label: "CTA Generator", icon: MessageSquarePlus },
  { value: "expand", label: "Expand Idea", icon: Lightbulb },
  { value: "rewrite", label: "Rewrite in My Voice", icon: Pencil },
  { value: "smart", label: "Smart Tools", icon: Zap },
] as const;

type SmartTool = {
  value: string;
  label: string;
  icon: React.ElementType;
  description: string;
};

const SMART_TOOLS: SmartTool[] = [
  { value: "make-this-better", label: "Make This Better", icon: Sparkles, description: "Improve clarity, flow, and strength while preserving your exact voice." },
  { value: "tone-guard", label: "Tone Guard", icon: ShieldCheck, description: "Detect and remove AI-sounding, robotic, or generic phrasing." },
  { value: "voice-strength", label: "Voice Strength Slider", icon: SlidersHorizontal, description: "Adjust how much personality comes through — subtle to strong." },
  { value: "clarity-personality", label: "Clarity vs Personality", icon: BarChart2, description: "Balance readability and personality without sacrificing either." },
  { value: "what-would-i-say", label: "What Would I Actually Say", icon: MessageCircle, description: "Rewrite overly formal text into your natural voice." },
  { value: "consistency-enforcer", label: "Consistency Enforcer", icon: Check, description: "Keep tone consistent when it drifts across a long text." },
  { value: "emotional-tone", label: "Emotional Tone Balancer", icon: Heart, description: "Adjust emotional intensity without changing your voice." },
  { value: "specificity-enhancer", label: "Specificity Enhancer", icon: Target, description: "Replace vague wording with concrete, vivid details." },
  { value: "credibility-booster", label: "Credibility Booster", icon: Star, description: "Make writing more convincing while keeping it natural." },
  { value: "overcomplication-reducer", label: "Overcomplication Reducer", icon: Scissors, description: "Simplify complex text without dumbing it down." },
];

export default function CreatorTools() {
  const { toast } = useToast();
  const sessionId = useAppStore((s) => s.sessionId);
  const sessionHeaders = { headers: { "x-session-id": sessionId } };
  const searchString = useSearch();

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(searchString);
    return params.get("tab") ?? "hooks";
  });
  
  const [hookTopic, setHookTopic] = useState("");
  const [hookPlatform, setHookPlatform] = useState<Platform>("twitter");
  const [hookResults, setHookResults] = useState<string[]>([]);

  const [threadTopic, setThreadTopic] = useState("");
  const [threadCount, setThreadCount] = useState(5);
  const [threadResults, setThreadResults] = useState<string[]>([]);

  const [hashContent, setHashContent] = useState("");
  const [hashPlatform, setHashPlatform] = useState<Platform>("instagram");
  const [hashResults, setHashResults] = useState<string[]>([]);

  const [ctaContent, setCtaContent] = useState("");
  const [ctaGoal, setCtaGoal] = useState<GenerateCTARequestGoal>("engage");
  const [ctaResults, setCtaResults] = useState<string[]>([]);

  const [repurposePost, setRepurposePost] = useState("");
  const [repurposeSource, setRepurposeSource] = useState<Platform>("twitter");
  const [repurposeTargets, setRepurposeTargets] = useState<Platform[]>([]);
  const [repurposeResults, setRepurposeResults] = useState<Record<string, string>>({});

  const [expandIdea, setExpandIdea] = useState("");
  const [expandPlatform, setExpandPlatform] = useState<Platform>("twitter");
  const [expandResults, setExpandResults] = useState<{ angles: string[]; relatedTopics: string[]; suggestedHooks: string[] } | null>(null);

  const [rewriteSource, setRewriteSource] = useState("");
  const [rewritePlatform, setRewritePlatform] = useState<Platform>("linkedin");
  const [rewriteTone, setRewriteTone] = useState(50);
  const [rewriteLoading, setRewriteLoading] = useState(false);
  const [rewriteResult, setRewriteResult] = useState<{ content: string; voiceMatchScore: number; voiceMatchRationale?: string; postId?: string; hasVoice?: boolean } | null>(null);
  const [rewriteSavedToVault, setRewriteSavedToVault] = useState(false);
  const [rewriteFeedbackGiven, setRewriteFeedbackGiven] = useState<"up" | "down" | null>(null);
  const [rewriteFeedbackMessage, setRewriteFeedbackMessage] = useState<string | null>(null);

  const [smartTool, setSmartTool] = useState<string>(() => {
    const params = new URLSearchParams(searchString);
    const toolParam = params.get("tool") ?? "";
    return SMART_TOOLS.some(t => t.value === toolParam) ? toolParam : "";
  });
  const [smartText, setSmartText] = useState("");
  const [smartContext, setSmartContext] = useState("");
  const [smartVoiceStrength, setSmartVoiceStrength] = useState(3);
  const [smartLoading, setSmartLoading] = useState(false);
  const [smartResult, setSmartResult] = useState<string | null>(null);
  const [smartCopied, setSmartCopied] = useState(false);

  const handleSmartTool = async () => {
    if (!smartText.trim()) return;
    setSmartLoading(true);
    setSmartResult(null);
    setSmartCopied(false);
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const body: Record<string, unknown> = { tool: smartTool, text: smartText };
      if (smartContext.trim()) body.context = smartContext;
      if (smartTool === "voice-strength") body.voiceStrength = smartVoiceStrength;
      const res = await fetch(`${baseUrl}/api/tools/smart`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Request failed" }));
        throw new Error(err.error ?? "Failed to run smart tool");
      }
      const data = await res.json() as { result: string; tool: string };
      setSmartResult(data.result);
    } catch (err) {
      toast({ title: "Smart tool failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    } finally {
      setSmartLoading(false);
    }
  };

  const handleSmartCopy = () => {
    if (!smartResult) return;
    navigator.clipboard.writeText(smartResult);
    setSmartCopied(true);
    toast({ title: "Copied!" });
    setTimeout(() => setSmartCopied(false), 1500);
  };

  const hooksMutation = useGenerateHooks({
    mutation: { onSuccess: (data) => setHookResults(data.hooks) },
    request: sessionHeaders,
  });

  const threadMutation = useGenerateThread({
    mutation: { onSuccess: (data) => setThreadResults([data.hookTweet, ...data.tweets, data.ctaTweet]) },
    request: sessionHeaders,
  });

  const hashtagMutation = useGenerateHashtags({
    mutation: { onSuccess: (data) => setHashResults(data.hashtags) },
    request: sessionHeaders,
  });

  const ctaMutation = useGenerateCTA({
    mutation: { onSuccess: (data) => setCtaResults(data.ctas) },
    request: sessionHeaders,
  });

  const repurposeMutation = useRepurposePost({
    mutation: { onSuccess: (data) => setRepurposeResults(data.repurposed) },
    request: sessionHeaders,
  });

  const expandMutation = useExpandIdea({
    mutation: { onSuccess: (data) => setExpandResults({ angles: data.angles, relatedTopics: data.relatedTopics, suggestedHooks: data.suggestedHooks }) },
    request: sessionHeaders,
  });

  const createIdeaMutation = useCreateIdea({
    mutation: {
      onSuccess: () => {
        setRewriteSavedToVault(true);
        toast({ title: "Saved to Vault!" });
      },
      onError: () => {
        toast({ title: "Failed to save", variant: "destructive" });
      },
    },
    request: sessionHeaders,
  });

  const rewriteFeedbackMutation = useSubmitVoiceFeedback({
    mutation: {
      onSuccess: (data) => {
        setRewriteFeedbackMessage(data.message);
      },
      onError: () => {
        setRewriteFeedbackMessage("Feedback recorded");
      },
    },
    request: sessionHeaders,
  });

  const handleRepurposeSourceChange = (newSource: Platform) => {
    setRepurposeSource(newSource);
    setRepurposeTargets((prev) => prev.filter((p) => p !== newSource));
  };

  const toggleRepurposeTarget = (platform: Platform) => {
    setRepurposeTargets((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const handleRewrite = async () => {
    if (!rewriteSource.trim()) return;
    setRewriteLoading(true);
    setRewriteResult(null);
    setRewriteSavedToVault(false);
    setRewriteFeedbackGiven(null);
    setRewriteFeedbackMessage(null);
    try {
      const baseUrl = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await fetch(`${baseUrl}/api/tools/rewrite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-session-id": sessionId },
        body: JSON.stringify({ sourceContent: rewriteSource, targetPlatform: rewritePlatform, tone: rewriteTone }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Request failed" }));
        throw new Error(err.error ?? "Failed to rewrite");
      }
      const data = await res.json() as { content: string; voiceMatchScore: number; voiceMatchRationale?: string; postId?: string; hasVoice?: boolean };
      setRewriteResult(data);
    } catch (err) {
      toast({ title: "Rewrite failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    } finally {
      setRewriteLoading(false);
    }
  };

  const isAnyLoading = hooksMutation.isPending || threadMutation.isPending || hashtagMutation.isPending || ctaMutation.isPending || repurposeMutation.isPending || expandMutation.isPending || rewriteLoading || smartLoading;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-5xl mx-auto"
    >
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold">Creator Tools</h1>
        <p className="text-muted-foreground mt-2">Mini-tools to scale your content creation.</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
        <div className="block sm:hidden">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            aria-label="Select creator tool"
            className="w-full h-12 px-4 rounded-xl bg-card border border-border text-sm font-medium appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary"
            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}
          >
            {TOOL_TABS.map((tab) => (
              <option key={tab.value} value={tab.value}>{tab.label}</option>
            ))}
          </select>
        </div>
        <TabsList className="hidden sm:flex bg-card border border-border p-1 rounded-xl h-auto w-auto flex-wrap gap-1">
          {TOOL_TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger key={tab.value} value={tab.value} className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground py-2 px-4 whitespace-nowrap">
                <Icon className="w-4 h-4 mr-2 shrink-0" /> {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="card-glow bg-card rounded-2xl border border-border p-6 shadow-lg">

            <TabsContent value="hooks" className="m-0 space-y-4">
              <h3 className="font-semibold text-lg">Generate Viral Hooks</h3>
              <p className="text-sm text-muted-foreground">What topic are you writing about?</p>
              <Input
                value={hookTopic}
                onChange={(e) => setHookTopic(e.target.value)}
                placeholder="e.g. Why developers should learn marketing..."
                className="h-12 bg-background border-border"
              />
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Platform</label>
                <PlatformSelect value={hookPlatform} onValueChange={setHookPlatform} />
              </div>
              <Button
                onClick={() => {
                  if (!hookTopic) return;
                  setHookResults([]);
                  hooksMutation.mutate({ data: { topic: hookTopic, platform: hookPlatform as GenerateHooksRequestPlatform } });
                }}
                disabled={isAnyLoading || !hookTopic}
                className="w-full h-12 bg-primary"
              >
                {hooksMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Generate Hooks
              </Button>
            </TabsContent>

            <TabsContent value="thread" className="m-0 space-y-4">
              <h3 className="font-semibold text-lg">Thread Builder</h3>
              <p className="text-sm text-muted-foreground">Give me a rough premise for a thread.</p>
              <Textarea
                value={threadTopic}
                onChange={(e) => setThreadTopic(e.target.value)}
                placeholder="A step by step guide on how I scaled my SaaS..."
                className="min-h-[120px] bg-background border-border"
              />
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">
                  Tweet Count: {threadCount}
                </label>
                <Slider
                  value={[threadCount]}
                  onValueChange={([v]) => setThreadCount(v)}
                  min={3}
                  max={10}
                  step={1}
                  className="py-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>3 tweets</span>
                  <span>10 tweets</span>
                </div>
              </div>
              <Button
                onClick={() => {
                  if (!threadTopic) return;
                  setThreadResults([]);
                  threadMutation.mutate({ data: { topic: threadTopic, tweetCount: threadCount } });
                }}
                disabled={isAnyLoading || !threadTopic}
                className="w-full h-12 bg-primary"
              >
                {threadMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Draft Thread
              </Button>
            </TabsContent>

            <TabsContent value="repurpose" className="m-0 space-y-4">
              <h3 className="font-semibold text-lg">Repurpose Post</h3>
              <p className="text-sm text-muted-foreground">Transform your post for other platforms.</p>
              <Textarea
                value={repurposePost}
                onChange={(e) => setRepurposePost(e.target.value)}
                placeholder="Paste your original post here..."
                className="min-h-[120px] bg-background border-border"
              />
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Source Platform</label>
                <PlatformSelect value={repurposeSource} onValueChange={handleRepurposeSourceChange} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Target Platforms</label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.filter((p) => p.value !== repurposeSource).map((p) => (
                    <button
                      key={p.value}
                      onClick={() => toggleRepurposeTarget(p.value)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        repurposeTargets.includes(p.value)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-border text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      <SocialIcon platform={p.value} size={20} branded={repurposeTargets.includes(p.value)} />
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              <Button
                onClick={() => {
                  if (!repurposePost || repurposeTargets.length === 0) return;
                  setRepurposeResults({});
                  repurposeMutation.mutate({
                    data: {
                      originalPost: repurposePost,
                      sourcePlatform: repurposeSource as RepurposeRequestSourcePlatform,
                      targetPlatforms: repurposeTargets as RepurposeRequestTargetPlatformsItem[],
                    },
                  });
                }}
                disabled={isAnyLoading || !repurposePost || repurposeTargets.length === 0}
                className="w-full h-12 bg-primary"
              >
                {repurposeMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Repurpose Post
              </Button>
            </TabsContent>

            <TabsContent value="hashtags" className="m-0 space-y-4">
              <h3 className="font-semibold text-lg">Smart Hashtags</h3>
              <p className="text-sm text-muted-foreground">Paste your post to get relevant tags.</p>
              <Textarea
                value={hashContent}
                onChange={(e) => setHashContent(e.target.value)}
                placeholder="Paste post content here..."
                className="min-h-[120px] bg-background border-border"
              />
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Platform</label>
                <PlatformSelect value={hashPlatform} onValueChange={setHashPlatform} />
              </div>
              <Button
                onClick={() => {
                  if (!hashContent) return;
                  setHashResults([]);
                  hashtagMutation.mutate({ data: { content: hashContent, platform: hashPlatform as GenerateHashtagsRequestPlatform } });
                }}
                disabled={isAnyLoading || !hashContent}
                className="w-full h-12 bg-primary"
              >
                {hashtagMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Find Hashtags
              </Button>
            </TabsContent>

            <TabsContent value="cta" className="m-0 space-y-4">
              <h3 className="font-semibold text-lg">CTA Generator</h3>
              <p className="text-sm text-muted-foreground">We'll craft the perfect call-to-action.</p>
              <Textarea
                value={ctaContent}
                onChange={(e) => setCtaContent(e.target.value)}
                placeholder="Paste your post to analyze context..."
                className="min-h-[120px] bg-background border-border"
              />
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Goal</label>
                <Select value={ctaGoal} onValueChange={(v) => setCtaGoal(v as GenerateCTARequestGoal)}>
                  <SelectTrigger className="h-10 bg-background border-border">
                    <SelectValue placeholder="Select goal" />
                  </SelectTrigger>
                  <SelectContent>
                    {CTA_GOALS.map((g) => (
                      <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => {
                  if (!ctaContent) return;
                  setCtaResults([]);
                  ctaMutation.mutate({ data: { postContent: ctaContent, goal: ctaGoal } });
                }}
                disabled={isAnyLoading || !ctaContent}
                className="w-full h-12 bg-primary"
              >
                {ctaMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Generate CTAs
              </Button>
            </TabsContent>

            <TabsContent value="expand" className="m-0 space-y-4">
              <h3 className="font-semibold text-lg">Expand Idea</h3>
              <p className="text-sm text-muted-foreground">Turn a rough thought into content angles.</p>
              <Textarea
                value={expandIdea}
                onChange={(e) => setExpandIdea(e.target.value)}
                placeholder="e.g. Why most startups fail in the first year..."
                className="min-h-[120px] bg-background border-border"
              />
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Platform</label>
                <PlatformSelect value={expandPlatform} onValueChange={setExpandPlatform} />
              </div>
              <Button
                onClick={() => {
                  if (!expandIdea) return;
                  setExpandResults(null);
                  expandMutation.mutate({ data: { idea: expandIdea, platform: expandPlatform as ExpandIdeaRequestPlatform } });
                }}
                disabled={isAnyLoading || !expandIdea}
                className="w-full h-12 bg-primary"
              >
                {expandMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Expand Idea
              </Button>
            </TabsContent>

            <TabsContent value="rewrite" className="m-0 space-y-4">
              <h3 className="font-semibold text-lg">Rewrite in My Voice</h3>
              <p className="text-sm text-muted-foreground">Paste any content — a blog post, press release, email, or anyone else's draft — and we'll rewrite it in your exact voice.</p>
              <Textarea
                value={rewriteSource}
                onChange={(e) => setRewriteSource(e.target.value)}
                placeholder="Paste the content you want to rewrite…"
                className="min-h-[160px] bg-background border-border"
                maxLength={4000}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Up to 4,000 characters</span>
                <span>{rewriteSource.length}/4000</span>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">Platform</label>
                <PlatformSelect value={rewritePlatform} onValueChange={setRewritePlatform} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">
                  Tone: {rewriteTone < 30 ? "Casual" : rewriteTone > 70 ? "Professional" : "Balanced"}
                </label>
                <Slider
                  value={[rewriteTone]}
                  onValueChange={([v]) => setRewriteTone(v)}
                  min={0}
                  max={100}
                  step={5}
                  className="py-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Casual</span>
                  <span>Professional</span>
                </div>
              </div>
              <Button
                onClick={handleRewrite}
                disabled={isAnyLoading || !rewriteSource.trim()}
                className="w-full h-12 bg-primary"
              >
                {rewriteLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Rewrite in My Voice
              </Button>
            </TabsContent>

            <TabsContent value="smart" className="m-0 space-y-4" data-testid="smart-tools-tab">
              <h3 className="font-semibold text-lg">Smart Tools</h3>
              <p className="text-sm text-muted-foreground">10 precision tools that improve your writing without changing your voice.</p>

              <div className="grid grid-cols-2 gap-2">
                {SMART_TOOLS.map((tool) => {
                  const Icon = tool.icon;
                  const isSelected = smartTool === tool.value;
                  return (
                    <button
                      key={tool.value}
                      data-testid={`smart-tool-btn-${tool.value}`}
                      onClick={() => { setSmartTool(tool.value); setSmartResult(null); }}
                      className={`flex flex-col items-start gap-1 p-3 rounded-xl border text-left transition-all duration-200 ${
                        isSelected
                          ? "border-primary/60 bg-primary/10 text-foreground shadow-[0_0_12px_hsl(188_90%_42%_/_0.18)]"
                          : "border-border bg-card hover:border-primary/30 hover:bg-primary/5 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        <Icon className={`w-4 h-4 shrink-0 ${isSelected ? "text-primary" : ""}`} />
                        <span className="text-xs font-medium leading-tight">{tool.label}</span>
                      </span>
                      <span className="text-[10px] leading-tight text-muted-foreground/80 mt-0.5 line-clamp-2">{tool.description}</span>
                    </button>
                  );
                })}
              </div>

              {smartTool && (
                <AnimatePresence>
                  <motion.div
                    key="smart-input-section"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.18 }}
                    className="space-y-3"
                  >
                    <Textarea
                      value={smartText}
                      onChange={(e) => setSmartText(e.target.value)}
                      placeholder="Paste the text you want to improve…"
                      className="min-h-[140px] bg-background border-border"
                      maxLength={4000}
                      data-testid="smart-text-input"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Up to 4,000 characters</span>
                      <span>{smartText.length}/4000</span>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">
                        Context (optional)
                      </label>
                      <Textarea
                        value={smartContext}
                        onChange={(e) => setSmartContext(e.target.value)}
                        placeholder="e.g. This is for a LinkedIn post, the audience is founders…"
                        className="min-h-[60px] bg-background border-border text-sm"
                        maxLength={400}
                      />
                    </div>

                    {smartTool === "voice-strength" && (
                      <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 block">
                          Personality: {smartVoiceStrength <= 2 ? "Subtle" : smartVoiceStrength === 3 ? "Balanced" : "Strong"}
                        </label>
                        <Slider
                          value={[smartVoiceStrength]}
                          onValueChange={([v]) => setSmartVoiceStrength(v)}
                          min={1}
                          max={5}
                          step={1}
                          className="py-2"
                          data-testid="smart-voice-strength-slider"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>Subtle</span>
                          <span>Strong</span>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleSmartTool}
                      disabled={isAnyLoading || !smartText.trim()}
                      className="w-full h-12"
                      style={{ background: "hsl(188 90% 42%)", color: "hsl(222 50% 4%)" }}
                      data-testid="smart-run-btn"
                    >
                      {smartLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</>
                      ) : (
                        <><Zap className="w-4 h-4 mr-2" /> Run {SMART_TOOLS.find((t) => t.value === smartTool)?.label ?? "Tool"}</>
                      )}
                    </Button>
                  </motion.div>
                </AnimatePresence>
              )}
            </TabsContent>

          </div>

          <div className="card-glow bg-background rounded-2xl border border-border p-6 overflow-hidden flex flex-col">
            <h3 className="font-semibold text-lg mb-4 text-muted-foreground">Results</h3>
            <div className="flex-1 overflow-y-auto space-y-3">
              {isAnyLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-20 bg-card border border-border/50 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : (
                <>
                  <TabsContent value="hooks" className="m-0 space-y-3">
                    {hookResults.length > 0 ? hookResults.map((r, i) => (
                      <ResultCard key={i}>{r}<CopyButton text={r} /></ResultCard>
                    )) : <EmptyResults />}
                  </TabsContent>

                  <TabsContent value="thread" className="m-0 space-y-3">
                    {threadResults.length > 0 ? threadResults.map((r, i) => (
                      <ResultCard key={i}>
                        {i === 0 && <span className="text-xs font-semibold text-primary block mb-1">Hook</span>}
                        {i === threadResults.length - 1 && <span className="text-xs font-semibold text-amber-400 block mb-1">CTA</span>}
                        {i > 0 && i < threadResults.length - 1 && <span className="text-xs font-semibold text-muted-foreground block mb-1">{i}/</span>}
                        {r}
                        <CopyButton text={r} />
                      </ResultCard>
                    )) : <EmptyResults />}
                  </TabsContent>

                  <TabsContent value="repurpose" className="m-0">
                    {Object.keys(repurposeResults).length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(repurposeResults).map(([platform, content]) => (
                          <div key={platform} className="group relative bg-card border border-border rounded-xl p-4 shadow-sm hover:border-primary/50 transition-colors flex flex-col">
                            <span className="text-xs font-bold text-primary uppercase tracking-wider mb-2">{platformLabel(platform)}</span>
                            <p className="text-sm leading-relaxed flex-1 pr-8">{content}</p>
                            <CopyButton text={content} />
                          </div>
                        ))}
                      </div>
                    ) : <EmptyResults />}
                  </TabsContent>

                  <TabsContent value="hashtags" className="m-0 space-y-3">
                    {hashResults.length > 0 ? (
                      <>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {hashResults.map((tag, i) => (
                            <span key={i} className="px-2.5 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">{tag}</span>
                          ))}
                        </div>
                        <ResultCard>
                          {hashResults.join(" ")}
                          <CopyButton text={hashResults.join(" ")} />
                        </ResultCard>
                      </>
                    ) : <EmptyResults />}
                  </TabsContent>

                  <TabsContent value="cta" className="m-0 space-y-3">
                    {ctaResults.length > 0 ? ctaResults.map((r, i) => (
                      <ResultCard key={i}>{r}<CopyButton text={r} /></ResultCard>
                    )) : <EmptyResults />}
                  </TabsContent>

                  <TabsContent value="expand" className="m-0 space-y-3">
                    {expandResults ? (
                      <>
                        {expandResults.angles.length > 0 && (
                          <div className="space-y-2">
                            <span className="text-xs font-semibold text-primary uppercase tracking-wider">Content Angles</span>
                            {expandResults.angles.map((a, i) => (
                              <ResultCard key={i}>{a}<CopyButton text={a} /></ResultCard>
                            ))}
                          </div>
                        )}
                        {expandResults.suggestedHooks.length > 0 && (
                          <div className="space-y-2 pt-3">
                            <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Suggested Hooks</span>
                            {expandResults.suggestedHooks.map((h, i) => (
                              <ResultCard key={i}>{h}<CopyButton text={h} /></ResultCard>
                            ))}
                          </div>
                        )}
                        {expandResults.relatedTopics.length > 0 && (
                          <div className="space-y-2 pt-3">
                            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Related Topics</span>
                            <div className="flex flex-wrap gap-2">
                              {expandResults.relatedTopics.map((t, i) => (
                                <span key={i} className="px-2.5 py-1 bg-secondary text-xs rounded-full">{t}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : <EmptyResults />}
                  </TabsContent>

                  <TabsContent value="smart" className="m-0 space-y-4" data-testid="smart-result-panel">
                    {smartResult ? (
                      <div className="relative group bg-card border border-primary/20 rounded-xl p-4 shadow-sm">
                        <p className="text-xs font-semibold text-primary/70 uppercase tracking-wider mb-2">
                          {SMART_TOOLS.find((t) => t.value === smartTool)?.label ?? "Result"}
                        </p>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap pr-8" data-testid="smart-result-text">{smartResult}</p>
                        <button
                          onClick={handleSmartCopy}
                          className="absolute top-3 right-3 p-1.5 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors opacity-0 group-hover:opacity-100"
                          aria-label="Copy result"
                          data-testid="smart-copy-btn"
                        >
                          {smartCopied ? (
                            <Check className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                          )}
                        </button>
                      </div>
                    ) : <EmptyResults />}
                  </TabsContent>

                  <TabsContent value="rewrite" className="m-0 space-y-4">
                    {rewriteResult ? (
                      <>
                        <div className="flex items-center gap-3 mb-2">
                          <VoiceMatchRing score={rewriteResult.voiceMatchScore} size={56} />
                          <div>
                            <p className="text-sm font-semibold">Voice Match</p>
                            {rewriteResult.voiceMatchRationale && (
                              <p className="text-xs text-muted-foreground leading-snug">{rewriteResult.voiceMatchRationale}</p>
                            )}
                          </div>
                        </div>

                        {!rewriteResult.hasVoice && (
                          <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-primary/25 bg-primary/5">
                            <Mic className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <p className="text-xs text-muted-foreground leading-snug">
                              Train your voice first so we can rewrite this as you.{" "}
                              <Link href="/training" className="text-primary underline underline-offset-2 hover:text-primary/80">
                                Go to Voice Training
                              </Link>
                            </p>
                          </div>
                        )}

                        <ResultCard>
                          <p className="whitespace-pre-wrap text-sm leading-relaxed pr-8">{rewriteResult.content}</p>
                          <CopyButton text={rewriteResult.content} />
                        </ResultCard>

                        {!rewriteSavedToVault ? (
                          <div className="rounded-xl border border-primary/30 bg-primary/5 p-3">
                            <p className="text-xs text-primary font-medium mb-2 flex items-center gap-1.5">
                              <Save className="w-3.5 h-3.5" /> Keep this rewrite in your Idea Vault
                            </p>
                            <Button
                              className="w-full h-10 bg-primary hover:bg-primary/90 rounded-xl"
                              onClick={() => {
                                createIdeaMutation.mutate({
                                  data: {
                                    title: rewriteSource.slice(0, 100) || "Rewritten post",
                                    notes: rewriteResult.content,
                                    tags: [rewritePlatform],
                                    platform: rewritePlatform,
                                    source: "generator",
                                  } as never,
                                });
                              }}
                              disabled={createIdeaMutation.isPending}
                            >
                              {createIdeaMutation.isPending ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                              ) : (
                                <><Save className="w-4 h-4 mr-2" /> Save to Vault</>
                              )}
                            </Button>
                          </div>
                        ) : (
                          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-center gap-2">
                            <Check className="w-4 h-4 text-emerald-400" />
                            <p className="text-sm text-emerald-400 font-medium">Saved to your Idea Vault</p>
                          </div>
                        )}

                        <AnimatePresence>
                          {!rewriteFeedbackGiven && (
                            <motion.div
                              initial={{ opacity: 0, y: 6 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0 }}
                              className="flex items-center justify-center gap-3 pt-1"
                              data-testid="rewrite-feedback-row"
                            >
                              <span className="text-xs text-muted-foreground">Does this sound like you?</span>
                              <button
                                onClick={() => {
                                  if (!rewriteResult.postId || rewriteFeedbackGiven) return;
                                  setRewriteFeedbackGiven("up");
                                  rewriteFeedbackMutation.mutate({ data: { postId: rewriteResult.postId, rating: "up" } });
                                }}
                                data-testid="rewrite-feedback-up"
                                disabled={rewriteFeedbackMutation.isPending}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-muted-foreground border border-border hover:bg-green-500/10 hover:text-green-500 hover:border-green-500/30 transition-all"
                              >
                                <ThumbsUp className="w-3.5 h-3.5" /> Yes
                              </button>
                              <button
                                onClick={() => {
                                  if (!rewriteResult.postId || rewriteFeedbackGiven) return;
                                  setRewriteFeedbackGiven("down");
                                  rewriteFeedbackMutation.mutate({ data: { postId: rewriteResult.postId, rating: "down" } });
                                }}
                                data-testid="rewrite-feedback-down"
                                disabled={rewriteFeedbackMutation.isPending}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-muted-foreground border border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 transition-all"
                              >
                                <ThumbsDown className="w-3.5 h-3.5" /> Not quite
                              </button>
                            </motion.div>
                          )}
                          {rewriteFeedbackGiven && rewriteFeedbackMessage && (
                            <motion.p
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              className="text-center text-xs text-muted-foreground pt-1"
                              data-testid="rewrite-feedback-message"
                            >
                              {rewriteFeedbackGiven === "up" ? "👍 " : "👎 "}{rewriteFeedbackMessage}
                            </motion.p>
                          )}
                        </AnimatePresence>
                      </>
                    ) : <EmptyResults />}
                  </TabsContent>
                </>
              )}
            </div>
          </div>
        </div>
      </Tabs>
    </motion.div>
  );
}

function EmptyResults() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-muted-foreground opacity-50 py-12">
      <Sparkles className="w-12 h-12 mb-3" />
      <p>Run a tool to see magic happen</p>
    </div>
  );
}
